import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { ConditionNodeData, ConditionOperator } from '@repo/shared-types';

const OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not Contains' },
  { value: 'greater_than', label: 'Greater Than (>)' },
  { value: 'greater_than_or_equal', label: 'Greater Than or Equal (≥)' },
  { value: 'less_than', label: 'Less Than (<)' },
  { value: 'less_than_or_equal', label: 'Less Than or Equal (≤)' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
  { value: 'in_list', label: 'In List' },
];

interface ConditionConfigProps {
  data: ConditionNodeData;
  onChange: (data: ConditionNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

interface RuleRowProps {
  field: string;
  operator: ConditionOperator;
  value: string;
  onFieldChange: (v: string) => void;
  onOperatorChange: (v: ConditionOperator) => void;
  onValueChange: (v: string) => void;
  onRemove?: () => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

const RuleRow = ({
  field,
  operator,
  value,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: RuleRowProps) => (
  <div className="space-y-2 rounded-md border p-3">
    <div className="flex items-start justify-between">
      <label className="text-muted-foreground text-xs font-medium">Field</label>
      {onRemove && (
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onRemove}>
          <Trash2 size={12} />
        </Button>
      )}
    </div>
    <div className="flex gap-1">
      <VariableAutocomplete
        value={field}
        onChange={onFieldChange}
        placeholder="{{call.outcome}}"
        responseFieldsSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        className="flex-1"
      />
      <VariablePicker
        onSelect={(v) => onFieldChange(field + v)}
        responseFieldsSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        triggerType={triggerType}
      />
    </div>
    <Select value={operator} onValueChange={(v) => onOperatorChange(v as ConditionOperator)}>
      <SelectTrigger>
        <SelectValue placeholder="Select operator" />
      </SelectTrigger>
      <SelectContent>
        {OPERATORS.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            {op.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <div className="flex gap-1">
      <VariableAutocomplete
        value={value}
        onChange={onValueChange}
        placeholder="Value or {{variable}}"
        responseFieldsSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        className="flex-1"
      />
      <VariablePicker
        onSelect={(v) => onValueChange(value + v)}
        responseFieldsSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        triggerType={triggerType}
      />
    </div>
  </div>
);

export const ConditionConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: ConditionConfigProps) => {
  const update = (patch: Partial<ConditionNodeData>) => onChange({ ...data, ...patch });
  const rules = data.rules ?? [];
  const isCompound = rules.length > 0;

  const handleAddRule = () => {
    if (!isCompound) {
      update({
        rules: [
          { field: data.field, operator: data.operator, value: data.value },
          { field: '', operator: 'equals', value: '' },
        ],
        logic: data.logic ?? 'AND',
      });
    } else {
      update({ rules: [...rules, { field: '', operator: 'equals', value: '' }] });
    }
  };

  const handleRemoveRule = (idx: number) => {
    const updated = rules.filter((_, i) => i !== idx);
    if (updated.length <= 1) {
      const remaining = updated[0] ?? { field: '', operator: 'equals' as const, value: '' };
      update({
        field: remaining.field,
        operator: remaining.operator,
        value: remaining.value,
        rules: undefined,
        logic: undefined,
      });
    } else {
      update({ rules: updated });
    }
  };

  const handleUpdateRule = (
    idx: number,
    patch: Partial<{ field: string; operator: ConditionOperator; value: string }>,
  ) => {
    const updated = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    update({ rules: updated });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <VariableAutocomplete
          value={data.label}
          onChange={(v) => update({ label: v })}
          placeholder="Node label"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
      </div>

      {isCompound && (
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">Logic</label>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={data.logic === 'AND' || !data.logic ? 'default' : 'outline'}
              onClick={() => update({ logic: 'AND' })}
              className="flex-1"
            >
              AND
            </Button>
            <Button
              size="sm"
              variant={data.logic === 'OR' ? 'default' : 'outline'}
              onClick={() => update({ logic: 'OR' })}
              className="flex-1"
            >
              OR
            </Button>
          </div>
          <p className="text-muted-foreground text-[10px]">
            {data.logic === 'OR' ? 'Any rule must match' : 'All rules must match'}
          </p>
        </div>
      )}

      {isCompound ? (
        <div className="space-y-2">
          {rules.map((rule, idx) => (
            <RuleRow
              key={idx}
              field={rule.field}
              operator={rule.operator}
              value={rule.value}
              onFieldChange={(v) => handleUpdateRule(idx, { field: v })}
              onOperatorChange={(v) => handleUpdateRule(idx, { operator: v })}
              onValueChange={(v) => handleUpdateRule(idx, { value: v })}
              onRemove={() => handleRemoveRule(idx)}
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
              triggerType={triggerType}
            />
          ))}
        </div>
      ) : (
        <RuleRow
          field={data.field}
          operator={data.operator}
          value={data.value}
          onFieldChange={(v) => update({ field: v })}
          onOperatorChange={(v) => update({ operator: v })}
          onValueChange={(v) => update({ value: v })}
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          triggerType={triggerType}
        />
      )}

      <Button variant="outline" size="sm" className="w-full" onClick={handleAddRule}>
        <Plus size={14} className="mr-1" />
        Add Rule
      </Button>
    </div>
  );
};
